import { Controller } from "@hotwired/stimulus"
import { forceSimulation, forceManyBody, forceLink, forceCenter, forceCollide } from "d3-force"

// The social crawl as an explorable graph. Nodes arrive lazily: the page
// seeds the target studios, every click fetches that node's neighbors and
// merges them in. Expanded state is remembered so a second click only
// focuses the panel.
const ZONE_COLORS = {
  "Wynwood": "#F59E0B", "Brickell": "#6366F1", "Edgewater": "#10B981",
  "Coconut Grove": "#14B8A6", "Coral Gables": "#EF4444", "Aventura": "#8B5CF6"
}

export default class extends Controller {
  static targets = ["canvas", "panel"]
  static values = { initial: Object, nodeUrl: String }

  connect() {
    this.nodes = []
    this.links = []
    this.seen = new Set()
    this.seenLinks = new Set()
    this.expanded = new Set()
    this.svg = this.canvasTarget
    this.svg.addEventListener("click", (e) => this.onClick(e))
    const { width, height } = this.svg.getBoundingClientRect()
    this.simulation = forceSimulation([])
      .force("charge", forceManyBody().strength(-120))
      .force("link", forceLink([]).id(n => n.id).distance(70))
      .force("center", forceCenter(width / 2, height / 2))
      .force("collide", forceCollide().radius(n => this.radius(n) + 4))
      .on("tick", () => this.render())
    this.merge(this.initialValue)
  }

  disconnect() { this.simulation.stop() }

  merge({ nodes, links }) {
    for (const node of nodes || []) {
      if (this.seen.has(node.id)) continue
      this.seen.add(node.id)
      this.nodes.push(node)
    }
    for (const link of links || []) {
      const key = `${link.source}|${link.target}`
      if (this.seenLinks.has(key)) continue
      this.seenLinks.add(key)
      this.links.push({ ...link }) // copy: d3 mutates source/target into node refs
    }
    this.simulation.nodes(this.nodes)
    this.simulation.force("link").links(this.links)
    this.simulation.alpha(0.8).restart()
  }

  async onClick(event) {
    const id = event.target.closest("[data-node-id]")?.dataset.nodeId
    if (!id) return
    const [type, uuid] = id.split(":")
    const response = await fetch(`${this.nodeUrlValue}/${type}/${uuid}`)
    if (!response.ok) return
    const payload = await response.json()
    if (!this.expanded.has(id)) { this.expanded.add(id); this.merge(payload) }
    this.showPanel(payload.panel)
  }

  radius(node) {
    if (node.type === "venue") return 10 + Math.min(14, Math.sqrt(node.size || 0) / 25)
    return 6 + Math.min(12, Math.sqrt(node.size || 0) / 30)
  }

  color(node) {
    if (node.type === "venue") return ZONE_COLORS[node.zone] || "#9CA3AF"
    if (node.owned) return "#1D1733"
    return node.provider === "instagram" ? "#E1306C" : "#25F4EE"
  }

  render() {
    // Fit the whole graph: 356 studios spread far wider than the viewport,
    // so the viewBox tracks the node extent instead of clipping it.
    const xs = this.nodes.map(n => n.x), ys = this.nodes.map(n => n.y)
    if (xs.length) {
      const pad = 60
      const minX = Math.min(...xs) - pad, maxX = Math.max(...xs) + pad
      const minY = Math.min(...ys) - pad, maxY = Math.max(...ys) + pad
      this.svg.setAttribute("viewBox", `${minX} ${minY} ${maxX - minX} ${maxY - minY}`)
    }
    const line = (l) =>
      `<line x1="${l.source.x}" y1="${l.source.y}" x2="${l.target.x}" y2="${l.target.y}"
             stroke="#D1D5DB" stroke-width="1"/>`
    const circle = (n) =>
      `<g data-node-id="${n.id}" cursor="pointer">
         <circle cx="${n.x}" cy="${n.y}" r="${this.radius(n)}" fill="${this.color(n)}"
                 stroke="white" stroke-width="1.5"/>
         <text x="${n.x}" y="${n.y + this.radius(n) + 11}" text-anchor="middle"
               font-size="10" fill="#6B7280">${this.escape(n.label)}</text>
       </g>`
    this.svg.innerHTML = this.links.map(line).join("") + this.nodes.map(circle).join("")
  }

  showPanel(panel) {
    if (!panel) return
    const rows = (panel.rows || [])
      .map(([k, v]) => `<div class="flex justify-between py-0.5"><span class="text-gray-500">${this.escape(k)}</span><span class="font-semibold">${this.escape(String(v))}</span></div>`).join("")
    const links = (panel.links || [])
      .map(([label, href]) => `<a href="${this.escape(href)}" target="_blank" rel="noopener" class="block text-indigo-700 underline py-0.5">${this.escape(label)}</a>`).join("")
    this.panelTarget.innerHTML =
      `<h3 class="font-bold text-gray-900">${this.escape(panel.title)}</h3>
       <p class="text-xs text-gray-500 mb-2">${this.escape(panel.subtitle || "")}</p>
       ${rows}<div class="mt-2 border-t border-gray-100 pt-2">${links}</div>`
    this.panelTarget.classList.remove("hidden")
  }

  escape(s) {
    return String(s ?? "").replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
